import React, {ReactNode, useState, useMemo, useEffect, useRef} from 'react';
import {
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  FormHelperText,
  ListItemIcon,
  SelectProps,
  InputLabelProps,
  FormControlProps,
  ListSubheader,
  Chip,
  Button,
} from '@material-ui/core';
import cn from 'classnames';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import CheckIcon from '../../assets/icons/CheckIcon';
import {StudioPopover} from '../StudioPopover';
import {StudioCheckbox} from '../StudioCheckbox';
import './StudioSelect.scss';
import {StudioTextField} from '../StudioTextField';
import {Close, Search} from '@material-ui/icons';

export type StudioSelectProps = {
  options?: Record<string, string>;
  allOptions?: Record<string, string>; // this is used in a case of multiple chip select and there's filter on options - in order for the chips not to be empty
  className?: string;
  label?: string;
  additionalLabel?: React.ReactNode;
  additionalMenu?: React.ReactNode;
  order?: string[];
  disabledOptions?: Record<string, boolean>;
  groupedOptions?: Array<{
    name: string;
    options: Array<{value: string; text: string}>;
  }>;
  tooltip?: string;
  tooltipPlacement?: string;
  fixedTooltip?: boolean;
  helperText?: ReactNode;
  note?: string;
  id?: string;
  InputLabelProps?: InputLabelProps;
  SelectProps?: SelectProps;
  placeholder?: string;
  disablePlaceholder?: boolean;
  dataTestId?: string;
  getAuxiliaryLabel?: ((value: string) => string) | null;
  onChipDelete?: (value: string) => void;
  renderEndAdornment?: ((value: string) => ReactNode) | null;
  renderStartAdornment?: ((value: string) => ReactNode) | null;
  onLoadMore?: () => Promise<void>;
  hasMore?: boolean;
  disabled?: boolean;
  placeholderClassName?: string;
  search?: boolean;
  longMenuContainer?: boolean;
  showDone?: boolean;
} & FormControlProps & {'data-testid'?: string};

export const StudioSelect = ({
  options: initialOptions = {},
  allOptions = {},
  groupedOptions,
  className,
  label,
  tooltipPlacement = 'bottom',
  additionalLabel,
  additionalMenu,
  order,
  disabledOptions,
  tooltip,
  fixedTooltip = false,
  helperText,
  note,
  id,
  InputLabelProps = {},
  SelectProps = {},
  placeholder,
  disablePlaceholder,
  dataTestId,
  getAuxiliaryLabel,
  onChipDelete,
  renderEndAdornment,
  renderStartAdornment,
  onLoadMore,
  hasMore,
  disabled,
  placeholderClassName,
  search = false,
  longMenuContainer = false,
  showDone = true,
  ...rest
}: StudioSelectProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState(initialOptions);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');

  const areOptionsGrouped = groupedOptions != null;

  useEffect(() => {
    if (!areOptionsGrouped) {
      setOptions(initialOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  useEffect(() => {
    if (!areOptionsGrouped) {
      if (searchText.length > 0) {
        const result: Record<string, string> = {};

        Object.entries(initialOptions).forEach(([key, value]) => {
          if (value.toLowerCase().includes(searchText.toLowerCase())) {
            result[key] = value;
          }
        });
        setOptions(result);
      } else {
        setOptions(initialOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, initialOptions]);

  const selectId = useMemo(
    () => id || SelectProps.name || dataTestId || rest['data-testid'] || 'studio-select',
    [id, SelectProps.name, dataTestId, rest]
  );
  const isMultiSelect = useMemo(() => Boolean(SelectProps.multiple), [
    SelectProps.multiple,
  ]);
  const selectedValue = useMemo(() => {
    let selectedValue = SelectProps.value ?? '';

    if (isMultiSelect) {
      selectedValue = selectedValue || [];
    } else {
      const availableOptions = [
        ...Object.keys(options),
        ...(groupedOptions?.flatMap(group => group.options).map(option => option.value) ||
          []),
      ];

      if (!availableOptions.includes(String(selectedValue))) {
        selectedValue = '';
      }
    }

    return selectedValue;
  }, [SelectProps.value, isMultiSelect, options, groupedOptions]);

  const handleLoadMore = async () => {
    if (onLoadMore) {
      setIsLoadingMore(true);
      await onLoadMore();
      setIsLoadingMore(false);
    }
  };

  const getOrderedOptions = (options: Record<string, string>, order?: string[]) => {
    if (order && order.length) {
      return order.map(value => [value, options[value]]);
    }
    return Object.entries(options).sort((a, b) => a[1].localeCompare(b[1]));
  };

  const renderOption = (value: string, text: string) => {
    const isSelected =
      isMultiSelect && Array.isArray(selectedValue)
        ? selectedValue.indexOf(value) !== -1
        : selectedValue === value;
    return (
      <MenuItem
        key={value}
        value={value}
        disabled={Boolean(disabledOptions?.[value])}
        divider
        className={cn(
          'studio-select__item',
          isSelected && 'studio-select__item--selected',
          isSelected &&
            (isMultiSelect || areOptionsGrouped) &&
            'studio-select__item--plain-selected'
        )}
        data-testid="studio-select-item"
        id={`${selectId}-${value}`}
      >
        {isMultiSelect && (
          <StudioCheckbox name={value} checked={isSelected} size="small" />
        )}
        {renderStartAdornment && (
          <div className="studio-select__start-icon">{renderStartAdornment(value)}</div>
        )}
        <span className="studio-select__text">{text}</span>
        {getAuxiliaryLabel && (
          <span className="studio-select__auxiliary-label">
            {getAuxiliaryLabel(value)}
          </span>
        )}
        {!isMultiSelect && isSelected && (
          <ListItemIcon className="studio-select__selected-icon">
            <CheckIcon />
          </ListItemIcon>
        )}
        {renderEndAdornment && (
          <div className="studio-select__end-icon">{renderEndAdornment(value)}</div>
        )}
      </MenuItem>
    );
  };

  const renderMultipleValues = (selected: unknown) => {
    if (Array.isArray(selected)) {
      if (selected.length === 0 && placeholder) {
        return placeholder;
      }
      return (
        <div className="studio-select__chips-container">
          {selected.map((value: string, i) => {
            const text = areOptionsGrouped
              ? groupedOptions
                  ?.flatMap(({options}) => options)
                  .find(option => option.value === value)?.text
              : initialOptions[value] || allOptions[value];
            return (
              <Chip
                disabled={disabled}
                key={i}
                label={text}
                variant="outlined"
                onMouseDown={e => {
                  e.stopPropagation();
                }}
                onDelete={onChipDelete ? () => onChipDelete(value) : undefined}
              />
            );
          })}
        </div>
      );
    }
  };

  return (
    <>
      <FormControl
        className={cn('studio-select', className)}
        disabled={disabled}
        {...rest}
      >
        {label && (
          <div className="studio-select__row">
            <InputLabel shrink {...InputLabelProps}>
              {label}
            </InputLabel>
            {additionalLabel}
            {(tooltip || fixedTooltip) && tooltipPlacement === 'top' && (
              <StudioPopover
                infoIconClass={cn(
                  'studio-select__tooltip',
                  fixedTooltip && !tooltip && 'studio-select__tooltip--hidden'
                )}
                infoMessage={tooltip}
              />
            )}
          </div>
        )}
        <div className="studio-select__row">
          <Select
            ref={ref}
            tabIndex={-1}
            id={selectId}
            IconComponent={ChevronDownIcon}
            MenuProps={{
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              className: cn(
                'studio-select__menu',
                longMenuContainer && 'studio-select__menu__long'
              ),
              TransitionProps: {
                onExited: (e: any) => {
                  e.target?.blur();
                  ref.current?.focus();
                },
              },
            }}
            disableUnderline
            displayEmpty={!!placeholder}
            data-testid={dataTestId ?? 'studio-select'}
            renderValue={isMultiSelect ? renderMultipleValues : undefined}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            {...SelectProps}
            value={selectedValue}
          >
            {!isMultiSelect && placeholder && (
              <MenuItem
                value=""
                disabled={disablePlaceholder}
                className={cn(
                  'studio-select__item',
                  placeholderClassName ? placeholderClassName : ''
                )}
                data-testid="studio-select-item"
                id={`${selectId}-placeholder`}
              >
                {placeholder}
              </MenuItem>
            )}
            {search && (
              <MenuItem className="studio-select__search">
                <StudioTextField
                  id="create-deploy-deployment-name"
                  className="create-prod-deploy__field text_field"
                  placeholder="Search..."
                  InputProps={{
                    value: searchText,
                    onChange: e => setSearchText(e.target.value),
                    startAdornment: <Search />,
                    endAdornment: (
                      <Close
                        className="studio-select__close"
                        onClick={() => setSearchText('')}
                      />
                    ),
                    autoFocus: true,
                  }}
                  onKeyDown={e => e.stopPropagation()}
                  autoFocus
                  onClick={e => e.stopPropagation()}
                />
              </MenuItem>
            )}
            {additionalMenu}
            {areOptionsGrouped
              ? groupedOptions?.flatMap(({name, options}) => [
                  <ListSubheader
                    key={name}
                    disableSticky
                    className="studio-select__group-header"
                  >
                    {name}
                  </ListSubheader>,
                  ...options.map(({value, text}) => renderOption(value, text)),
                ])
              : getOrderedOptions(options, order).map(([optionValue, text]) =>
                  renderOption(optionValue, text)
                )}
            {isMultiSelect && showDone && (
              <div
                className="studio-select__menu-footer"
                onClick={event => {
                  event.stopPropagation();
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={event => {
                    event.stopPropagation();
                    setIsOpen(false);
                  }}
                >
                  Done
                </Button>
              </div>
            )}
            {onLoadMore && hasMore && (
              <div
                className="studio-select__menu-footer"
                onClick={event => {
                  event.stopPropagation();
                }}
              >
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={isLoadingMore}
                    onClick={event => {
                      event.stopPropagation();
                      handleLoadMore();
                    }}
                  >
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </span>
              </div>
            )}
          </Select>
          {(tooltip || fixedTooltip) && tooltipPlacement === 'bottom' && (
            <StudioPopover
              infoIconClass={cn(
                'studio-select__tooltip',
                fixedTooltip && !tooltip && 'studio-select__tooltip--hidden'
              )}
              infoMessage={tooltip}
            />
          )}
        </div>
        {note && <FormHelperText>{note}</FormHelperText>}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </>
  );
};
